/* eslint-disable @typescript-eslint/no-floating-promises */
import {
  AddressCard,
  Box,
  Grid,
  ReassignContactCard,
  Stack,
  Tooltip,
} from '@atgfr8/atg-ui-tailwind';
import {
  AccountCircleIcon,
  EmailIcon,
  InformationIcon,
  PhoneIcon,
} from '@atgfr8/icons';
import { ContactDropdown, Popover } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  CarrierContactT,
  UserInfoModel,
  customerStore,
  fetchUserInfo,
  fetchUserInfoUserModel,
  isCreditLimitExceeded,
  orderContainsLTLLoad,
  orderStore,
  setOrderStore,
  updateOrderState,
} from '@store/orders';
import { applyOrderHydration } from '@store/orders/actions/orderFormActions';
import { ICustomerContact } from '@typeDefinitions/contactTypes';
import { CustomerContactViewModel } from '@typeDefinitions/customerTypes';
import { formatContactPhone } from '@utils/phoneNumberFormat';
import { canReassign, formatAmount, printLog } from '@utils/utils';
import { get, isEmpty } from 'lodash';
import {
  Accessor,
  Component,
  Setter,
  Show,
  createEffect,
  createSignal,
  onMount,
} from 'solid-js';

import ReassignCard from '../reassign/ReassignCard';
import classes from './classes';
import { OperationsRep } from './OperationsRep';
type CustomerContactInformationDetailsProps = {
  orderRepInformation: Accessor<UserInfoModel | undefined>;
  setOrderRepInformation: Setter<UserInfoModel | undefined>;
};

export const isCustomerPresent = () => orderStore.order.customerId !== null;

const CustomerContactInformationDetails: Component<
  CustomerContactInformationDetailsProps
> = (props: CustomerContactInformationDetailsProps) => {
  const [isLoading, setIsLoading] = createSignal(false);
  const [accountManagerInformation, setAccountManagerInformation] =
    createSignal<CustomerContactViewModel>();
  const [salesManagerInformation, setSalesManagerInformation] =
    createSignal<CustomerContactViewModel>();

  createEffect(async () => {
    if (Boolean(orderStore.order.assignedId)) {
      const response = await fetchUserInfo(orderStore.order.assignedId);
      setOrderStore('order', 'orderRep', {
        office: response.office,
        name: response.name,
        phone1: response.phone1,
        email: response.email,
        accountingGroup: response.accountingGroup ?? '',
      });
    }
  });

  const cls = classes.customerInfo;

  const getContact = (): ICustomerContact => {
    return [
      ...customerStore.customer.billingContacts,
      ...customerStore.customer.freightContacts,
    ].find(
      (item) => item.id === Number(orderStore.order.customerContactId),
    ) as ICustomerContact;
  };

  const getCustomerInformation = async (id: number, type: string) => {
    if (!isCustomerPresent()) return;

    try {
      setIsLoading(true);

      let response;
      switch (type) {
        case 'SALE':
          if (isEmpty(salesManagerInformation())) {
            response = await fetchUserInfo(id);
            setSalesManagerInformation(response);
          }
          break;

        case 'ACCOUNT':
          if (isEmpty(accountManagerInformation())) {
            response = await fetchUserInfo(id);
            setAccountManagerInformation(response);
          }
          break;

        case 'ORDER REP':
          response = await fetchUserInfoUserModel(id);
          props.setOrderRepInformation(response);
          updateOrderState({
            owner: response.name,
            assignedId: response.id,
          });

          break;

        default:
          break;
      }
    } catch (err) {
      printLog(err, 'ERROR');
    } finally {
      setIsLoading(false);
    }
  };

  const emailLink = () =>
    [
      ...customerStore.customer.billingContacts,
      ...customerStore.customer.freightContacts,
    ].find((item) => item.id === Number(orderStore.order.customerContactId))
      ?.email;

  const iconStyle = () =>
    !isCustomerPresent() ? cls.disabledPhoneIconStyle : cls.phoneIconStyle;

  const getCustomerContactId = () => {
    const { customerContactId } = orderStore.order;
    const tempContact: ICustomerContact[] = [
      ...customerStore.customer.freightContacts,
      ...customerStore.customer.billingContacts,
    ];

    updateOrderState({
      customerContactId:
        Number(customerContactId) ||
        (tempContact.length > 0 ? tempContact[0].id : null),
    });
  };

  onMount(() => applyOrderHydration(getCustomerContactId));

  return (
    <>
      <Box class="my-6! [&_.floating-label]:bg-transparent! [&_.floating-label_.strip]:hidden!">
        <ContactDropdown
          contacts={
            [
              ...customerStore.customer.freightContacts,
              ...customerStore.customer.billingContacts,
            ] as CarrierContactT[]
          }
          onChange={(item) => {
            updateOrderState({ customerContactId: item });
          }}
          contactId={Number(orderStore.order.customerContactId)}
          disabled={!isCustomerPresent() || orderStore.isReadOnly}
          respectVerification={false}
          small
        />
      </Box>
      <Show when={!orderStore.isCreate}>
        <Box class={`${cls.rowContainer} text-[#026EA1]`}>
          <Box class="font-semibold text-sm">
            <PhoneIcon
              size={14}
              class="inline-block"
              style={{ ...iconStyle(), 'margin-top': '-1px' }}
            />{' '}
            {formatContactPhone(getContact() as CarrierContactT, 'display')}
          </Box>
          <Box class="font-semibold text-sm">
            <EmailIcon
              size={14}
              class="inline-block"
              style={{ ...iconStyle(), 'margin-top': '-1px' }}
            />{' '}
            <a href={`mailto:${emailLink() ?? '-'}`}>{emailLink() ?? '-'}</a>
          </Box>
        </Box>
      </Show>
      <div></div>
      <Box class={orderStore.isCreate ? 'mt-[16px]' : ''}>
        <Stack spacing={1}>
          <Grid container>
            <Grid item size={3}>
              <Box
                class={`inline-flex text-sm items-center ${isFeatureFlagEnabled(FeatureFlags.feature_order_block_save_on_neg_credit_limit)() && !orderContainsLTLLoad() && isCreditLimitExceeded() ? 'text-[#B00020]! font-semibold' : 'text-[#666666]!'}`}
              >
                Credit
              </Box>
            </Grid>
            <Grid item size="auto" class="flex items-center">
              <Box
                class={`text-sm ${isFeatureFlagEnabled(FeatureFlags.feature_order_block_save_on_neg_credit_limit)() && !orderContainsLTLLoad() && isCreditLimitExceeded() ? 'text-[#B00020] font-semibold' : ''}`}
              >
                <Show
                  when={
                    get(customerStore.customer, 'available') &&
                    isCustomerPresent()
                  }
                  fallback="-"
                >
                  {formatAmount(
                    orderStore.order.customer?.existingCustomerCredit ??
                      get(customerStore.customer, 'available', 0),
                  )}{' '}
                </Show>
                /{' '}
                {get(customerStore.customer, 'creditLimit') &&
                isCustomerPresent()
                  ? formatAmount(get(customerStore.customer, 'creditLimit', 0))
                  : '-'}
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            onClick={() => {
              getCustomerInformation(
                get(customerStore, 'customer.accountManagerId'),
                'ACCOUNT',
              );
            }}
          >
            <Grid item size={3}>
              <Box class="text-[#666666]! inline-flex text-sm items-center">
                Account Manager
              </Box>
            </Grid>
            <Grid item size="auto">
              <Popover
                eleName={
                  <Box class="inline-flex">
                    <AccountCircleIcon style={iconStyle()} size={20} />
                    <Show when={isCustomerPresent()}>
                      <Box class="ml-2 text-sm">
                        {get(customerStore.customer, 'accountManager', '')}
                      </Box>
                    </Show>
                  </Box>
                }
                onClick={() => {
                  getCustomerInformation(
                    get(customerStore, 'customer.accountManagerId'),
                    'ACCOUNT',
                  );
                }}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sxProps={{
                  'text-transform': 'none',
                  padding: '0px',
                  'min-width': '0px',
                  'pointer-events': isCustomerPresent() ? 'auto' : 'none',
                }}
                class="hover:bg-none"
              >
                <AddressCard
                  contact={
                    accountManagerInformation() as unknown as CustomerContactViewModel
                  }
                  mode="regular"
                  showIcon
                  isLoading={isLoading()}
                />
              </Popover>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item size={3}>
              <Box class="text-[#666666]! inline-flex text-sm items-center">
                Sales Manager
              </Box>
            </Grid>
            <Grid item size="auto">
              <Popover
                eleName={
                  <Box class="inline-flex">
                    <AccountCircleIcon style={iconStyle()} size={20} />
                    <Show when={isCustomerPresent()}>
                      <Box class="ml-2 text-sm">
                        {get(customerStore.customer, 'salesManager', '')}
                      </Box>
                    </Show>
                  </Box>
                }
                onClick={() =>
                  getCustomerInformation(
                    get(customerStore, 'customer.salesManagerId'),
                    'SALE',
                  )
                }
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sxProps={{
                  'text-transform': 'none',
                  padding: '0px',
                  'min-width': '0px',
                  'pointer-events': isCustomerPresent() ? 'auto' : 'none',
                }}
                class="hover:bg-none"
              >
                <AddressCard
                  contact={salesManagerInformation()}
                  mode="regular"
                  showIcon
                  isLoading={isLoading()}
                />
              </Popover>
            </Grid>
          </Grid>
          <Grid container class="flex text-sm items-center">
            <Grid item size={3}>
              <Box class="text-[#666666]! flex flex-col">
                Order Rep
                <Box class="pl-[10px] mt-1">
                  <Tooltip
                    class="text-base max-w-[300px] z-9999"
                    title="Load will be visible to everyone in the same office as selected Order Rep."
                    placement="bottom-start"
                    dataTestId="order-rep-visibility-tooltip"
                  >
                    <InformationIcon class="text-[#026EA1]! cursor-pointer" />
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
            <Grid item size="auto" class="inline-flex ml-auto items-center">
              <Popover
                eleName={
                  <Box class="inline-flex ml-auto">
                    <AccountCircleIcon style={iconStyle()} size={20} />
                    <Show when={isCustomerPresent()}>
                      <Box class="flex ml-2 text-small">
                        {get(orderStore.order, 'owner', '')}
                      </Box>
                    </Show>
                  </Box>
                }
                onClick={() => {
                  void getCustomerInformation(
                    get(orderStore, 'order.assignedId'),
                    'ORDER REP',
                  );
                }}
                sxProps={{
                  'text-transform': 'none',
                  padding: '0px',
                  'min-width': '0px',
                  'pointer-events': isCustomerPresent() ? 'auto' : 'none',
                }}
                class="hover:bg-none"
              >
                <ReassignContactCard
                  contact={props.orderRepInformation()}
                  showIcon
                  isLoading={isLoading()}
                />
              </Popover>
              <Show
                when={
                  isCustomerPresent() && canReassign() && !orderStore.isReadOnly
                }
              >
                <Popover
                  eleName={<div class="text-[#026EA1] text-sm">REASSIGN</div>}
                  onClick={() => {
                    void getCustomerInformation(
                      get(orderStore, 'order.assignedId'),
                      'ORDER REP',
                    );
                  }}
                >
                  <ReassignCard
                    type={'ORDER REP'}
                    contactInfo={props.orderRepInformation()}
                    isLoading={isLoading}
                    onButtonClick={getCustomerInformation}
                    disabled={orderStore.isReadOnly}
                  />
                </Popover>
              </Show>
            </Grid>
          </Grid>
          <OperationsRep />
        </Stack>
      </Box>
    </>
  );
};

export default CustomerContactInformationDetails;
